import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Send, MessageCircle, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { createInvite, fetchMyActiveInvite, cancelInvite, buildInviteUrl } from '../lib/invites.js';

export function InvitePartnerModal({ theme, onClose }) {
  const [activeInvite, setActiveInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchMyActiveInvite().then(invite => {
      setActiveInvite(invite);
      setLoading(false);
    });
  }, []);

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    try {
      const invite = await createInvite(email.trim() || null);
      setActiveInvite(invite);
    } catch (e) {
      alert('Kunne ikke oprette invitation: ' + e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleCancel() {
    if (!activeInvite) return;
    if (!confirm('Annullér invitationen? Du kan altid lave en ny.')) return;
    try {
      await cancelInvite(activeInvite.id);
      setActiveInvite(null);
      setEmail('');
    } catch (e) {
      alert('Kunne ikke annullere: ' + e.message);
    }
  }

  function handleCopy() {
    if (!activeInvite) return;
    const url = buildInviteUrl(activeInvite.inviteCode);
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleShare(channel) {
    if (!activeInvite) return;
    const url = buildInviteUrl(activeInvite.inviteCode);
    const text = `Jeg har inviteret dig til at være min partner på Tag Team. Klik her for at acceptere: ${url}`;

    if (channel === 'sms') {
      window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
    } else if (channel === 'email') {
      window.location.href = `mailto:${email || ''}?subject=${encodeURIComponent('Tag Team — vil du være min partner?')}&body=${encodeURIComponent(text)}`;
    } else if (channel === 'messenger') {
      // Generic share URL
      if (navigator.share) {
        navigator.share({ text, url });
      } else {
        navigator.clipboard.writeText(text);
        alert('Tekst kopieret — indsæt den i din chat');
      }
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 200,
    }} onClick={onClose}>
      <div style={{
        background: theme.bgElevated,
        borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 560,
        maxHeight: '92vh',
        overflowY: 'auto',
        animation: 'tt-slide-up 0.3s ease-out',
      }} onClick={e => e.stopPropagation()}>
        <style>{`@keyframes tt-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Inviter partner</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: theme.textMuted,
            cursor: 'pointer', padding: 4,
          }}><X size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: theme.textMuted, padding: 30 }}>
              Henter...
            </div>
          ) : activeInvite ? (
            <ActiveInviteView
              theme={theme}
              invite={activeInvite}
              onCopy={handleCopy}
              copied={copied}
              onShare={handleShare}
              onCancel={handleCancel}
            />
          ) : (
            <CreateInviteView
              theme={theme}
              email={email}
              setEmail={setEmail}
              onCreate={handleCreate}
              creating={creating}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CreateInviteView({ theme, email, setEmail, onCreate, creating }) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 38, marginBottom: 8 }}>💕</div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Find din partner</h3>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
          Lav et invitation-link og send det til din partner. Når de accepterer, bliver I parret op i appen.
        </p>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          Partners email (valgfri)
        </label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="navn@example.com"
          style={{
            width: '100%',
            padding: '10px 12px',
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            color: theme.text,
            fontSize: 14,
            outline: 'none',
          }}
        />
        <div style={{ fontSize: 11, color: theme.textDim, marginTop: 6, lineHeight: 1.4 }}>
          Email er valgfri. Hvis du angiver den, kan vi sende invitationen direkte. Ellers laver vi bare et link du selv kan dele.
        </div>
      </div>

      <button onClick={onCreate} disabled={creating} style={{
        width: '100%',
        padding: '12px 16px',
        background: theme.coral,
        color: 'white',
        border: 'none',
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}>
        {creating ? 'Opretter...' : <><Send size={14} /> Opret invitation</>}
      </button>
    </div>
  );
}

function ActiveInviteView({ theme, invite, onCopy, copied, onShare, onCancel }) {
  const url = buildInviteUrl(invite.inviteCode);
  const expires = new Date(invite.expiresAt);
  const daysLeft = Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 38, marginBottom: 8 }}>📨</div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Invitation klar</h3>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
          Send linket til din partner. Når de klikker og logger ind, accepterer de invitationen.
        </p>
      </div>

      {/* Link box */}
      <div style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 14,
        wordBreak: 'break-all',
        fontFamily: 'monospace',
        fontSize: 12,
        color: theme.text,
      }}>{url}</div>

      {/* Action buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 8 }}>
        <button onClick={onCopy} style={{
          padding: '11px 12px',
          background: copied ? theme.teal : theme.bgCard,
          color: copied ? 'white' : theme.text,
          border: `1px solid ${copied ? theme.teal : theme.border}`,
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}>
          {copied ? <><Check size={14} /> Kopieret!</> : <><Copy size={14} /> Kopiér link</>}
        </button>

        <button onClick={() => onShare('messenger')} style={shareBtn(theme)}>
          <MessageCircle size={14} /> Del
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
        <button onClick={() => onShare('sms')} style={shareBtn(theme)}>
          <MessageCircle size={14} /> SMS
        </button>
        <button onClick={() => onShare('email')} style={shareBtn(theme)}>
          <Mail size={14} /> Email
        </button>
      </div>

      {/* Status */}
      <div style={{
        padding: '10px 14px',
        background: `${theme.gold}10`,
        border: `1px solid ${theme.gold}30`,
        borderRadius: 10,
        fontSize: 12,
        color: theme.gold,
        textAlign: 'center',
        marginBottom: 14,
        lineHeight: 1.5,
      }}>
        Venter på din partner · Udløber om {daysLeft} {daysLeft === 1 ? 'dag' : 'dage'}
      </div>

      {/* Cancel */}
      <button onClick={onCancel} style={{
        width: '100%',
        padding: '10px 12px',
        background: 'transparent',
        color: theme.textMuted,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}>
        <Trash2 size={12} /> Annullér invitation
      </button>
    </div>
  );
}

function shareBtn(theme) {
  return {
    padding: '11px 12px',
    background: theme.bgCard,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  };
}
